import axios from "axios";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import SectionAppBar from "../../common/SectionAppBar";
import Loader from "../../common/Loader";
import ArrowElement from "../Canvas/components/arrow/ArrowElement";
import SetTaskInfo from "../Tasks/SetTaskInfo";
import DoubleSidedArrowElement from "../Canvas/components/arrow/DoubleSidedArrowElement";
import LineElement from "../Canvas/components/line/LineElement";
import CircleElement from "../Canvas/components/circle/CircleElement";
import RectangleElement from "../Canvas/components/rectangle/RectangleElement";
import CanvasImage from "../Canvas/components/canvas-core/CanvasImage";
import { getLastSibling, getSibling } from "../../redux/partSlice";
import {
  calculateGroupHeight,
  calculateTextBlockWidth,
  estimateKonvaTextHeight,
  formatListContent,
  getTextStyle,
  hasNoteContent,
  hasTaskContent,
  hasUserContent,
  renderPreview,
  renderVideo,
  stripHtmlPreserveFormatting,
} from "./plugins/utils";
import TextElement from "../Canvas/components/text/TextElement";
import CanvasDisplay from "../Canvas/components/canvas-core/CanvasDisplay";
import "../../common/RichTextEditor/Editor/index.css";
import { BASE_URL } from "../../config";

const SelectedSectionElement = () => {
  const {
    partId,
    title,
    revisionId,
    substepId,
    type,
    stepTitle,
    richCount,
    subsCount,
    attachmentCount,
    videoCount,
    formCount,
    canvasCount,
    stepCount,
    partNumber,
    revision,
    section,
    stepId,
    sectionId,
    siblingOrder,
  } = useParams();
  const [counters, setCounters] = useState({
    richTextCounter: 0,
    attachmentCounter: 0,
    formCounter: 0,
    videoCounter: 0,
    canvasCounter: 0,
  });
  const [lastChildSibling, setLastChildSibling] = useState(0);
  const location = useLocation();
  const { data, hideParent } = location.state || {};
  const dispatch = useDispatch();

  // Get Redux state for substep calculation
  const { substepsByStep } = useSelector((state) => state.step);

  // Calculate substep information if this is a substep
  const isSubstep =
    substepId && substepId !== "null" && substepId !== "undefined";

  // Check if this is a nested substep (child of a substep)
  const isNestedSubstep = data?.fromSubstepId || data?.parentSubstepId;

  // Get substep index from Redux store
  const substepIndex =
    isSubstep && stepId
      ? (substepsByStep[stepId] || []).findIndex(
          (substep) => substep.sub_step_tree_node_id === substepId
        )
      : -1;

  const substepLetter =
    isSubstep && substepIndex >= 0
      ? String.fromCharCode(65 + substepIndex)
      : "";

  // Extract step number from stepTitle (case-insensitive)
  const stepNumberMatch = stepTitle ? stepTitle.match(/Step\s+(\d+)/i) : null;
  const stepNumber = stepNumberMatch ? stepNumberMatch[1] : "";

  // For nested substeps, we need to get the parent substep information
  let parentSubstepInfo = "";
  if (isNestedSubstep && data?.parentSubstepTitle) {
    // Extract parent substep letter from parentSubstepTitle
    const parentSubstepMatch =
      data.parentSubstepTitle.match(/Substep\s+\d+([A-Z])/i);
    const parentSubstepLetter = parentSubstepMatch ? parentSubstepMatch[1] : "";
    parentSubstepInfo = ` > Substep ${stepNumber}${parentSubstepLetter}`;
  }

  // Debug logging

  const substepTreePath =
    isSubstep && stepNumber && substepIndex >= 0
      ? `${section} > Step ${stepNumber} > Substep ${stepNumber}${substepLetter}${parentSubstepInfo}`
      : `${section} > ${stepTitle}`;

  const [newData, setNewData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchCounters = async () => {
      const richTextCount = await dispatch(
        getLastSibling({
          type: "rich-text",

          revisionId,
          partId,
        })
      );
      const attachmentCount = await dispatch(
        getLastSibling({
          type: "attachment",

          revisionId,
          partId,
        })
      );

      const formCount = await dispatch(
        getLastSibling({
          type: "form",

          revisionId,
          partId,
        })
      );
      const videoCount = await dispatch(
        getLastSibling({
          type: "video",

          revisionId,
          partId,
        })
      );
      const canvasCount = await dispatch(
        getLastSibling({
          type: "canva",

          revisionId,
          partId,
        })
      );
      const lastChildSiblingOrder = await dispatch(
        getSibling({
          type: "child",
          sectionId,
          stepId,
          revisionId,
          partId,
        })
      );
      setLastChildSibling(lastChildSiblingOrder?.payload);
      setCounters({
        richTextCounter: parseInt(richTextCount.payload) || 0,
        attachmentCounter: parseInt(attachmentCount.payload) || 0,
        formCounter: parseInt(formCount.payload) || 0,
        videoCounter: parseInt(videoCount.payload) || 0,
        canvasCounter: parseInt(canvasCount.payload) || 0,
      });

    };
    fetchCounters();
  }, [dispatch, stepId, sectionId, revisionId, partId, substepId]);

  useEffect(() => {
    let tskinfo = {};
    let url = "";

    if (!data) return;

    if (type === "video") {
      url = `video-tree-node/get-by-id/${data.video_tree_node_ref}`;
      tskinfo.refrence = data.video_tree_node_id;
      tskinfo.tree_node_id_ref = data.procedure_tree_node_id;
    } else if (type === "rich-text") {
      url = `rich-text-tree/get-by-id/${data.rich_text_node_id}`;
      tskinfo.refrence = data.rich_text_node_id;
      tskinfo.tree_node_id_ref = data.procedure_tree_ref;
    } else if (type === "attachment") {
      url = `attachment-tree/get-by-id/${data.attachment_tree_node_id}`;
      tskinfo.refrence = data.attachment_tree_node_id;
      tskinfo.tree_node_id_ref = data.tree_node_id;
    } else if (type === "canvas") {
      url = `canvas-tree/get-by-id/${data.canvas_tree_node_id}`;
      tskinfo.refrence = data.canvas_tree_node_id;
      tskinfo.tree_node_id_ref = data.procedure_tree_ref;
    }

    const fetchData = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${BASE_URL}${url}`);
        const responseData = response.data.results;

        setNewData(responseData);
        SetTaskInfo(tskinfo.refrence, tskinfo.tree_node_id_ref);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    dispatch,
    type,
    stepId,
    substepId,
    data?.video_tree_node_ref,
    data?.rich_text_node_id,
    data?.attachment_tree_node_id,
    data?.canvas_tree_node_id,
  ]);

  const renderVideo1 = (video) => {
    if (!video || !video.video) return null;

    // Check for video_url in multiple possible locations
    const videoUrl = video.video_url || 
                     video.detail?.video_url || 
                     (video.detail?.videos && video.detail.videos[0]?.video_url) ||
                     "";

    return (
      <div>
        <div style={{ marginBottom: "10px" }}>{video.short_description}</div>
        <div style={{ position: "relative" }}>
          <button
            type="button"
            className="icon-button"
            title="Fullscreen"
            onClick={() => {
              const videoElement = document.querySelector(".video-player");
              if (videoElement) {
                if (document.fullscreenElement) {
                  document.exitFullscreen();
                } else {
                  videoElement.requestFullscreen();
                }
              }
            }}
            style={{
              position: "absolute",
              top: "10px",
              right: "10px",
              zIndex: "10",
              background: "rgba(0, 0, 0, 0.5)",
              border: "none",
              borderRadius: "50%",
              padding: "6px",
              cursor: "pointer",
            }}
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 20 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ filter: "brightness(0) invert(1)" }}
            >
              <path
                d="M0 13.4124L7.39648 13.0675L3.39952 6.8344L0 13.4124ZM20 0.587402L12.6035 0.932333L16.6005 7.16541L20 0.587402ZM5.20435 10.8369L15.4879 4.24252L14.7957 3.16292L4.51205 9.75728L5.20435 10.8369Z"
                fill="currentColor"
              />
            </svg>
          </button>
          {renderVideo(video)}
        </div>
        {videoUrl && (
          <div style={{ marginTop: "10px" }}>{videoUrl}</div>
        )}
      </div>
    );
  };


  return (
    <div>
      <SectionAppBar
        title={title}
        path={`/add-section`}
        partId={partId}
        revisionId={revisionId}
        substepId={substepId}
        partNumber={partNumber}
        revision={revision}
        section={section}
        stepTitle={stepTitle}
        richCount={counters.richTextCounter}
        attachmentCount={counters.attachmentCounter}
        videoCount={counters.videoCounter}
        formCount={counters.formCounter}
        canvasCount={counters.canvasCounter}
        stepCount={stepCount}
        stepId={stepId}
        sectionId={sectionId}
        elementType={type}
        siblingOrder={
          siblingOrder.includes(",")
            ? siblingOrder.split(",")[0].trim()
            : siblingOrder
        }
        isSelectedPage={true}
        isSectionElement={true}
        id={
          type === "canvas"
            ? newData?.canvas_tree_node_id || data?.canvas_tree_node_id
            : type === "rich-text"
            ? newData?.rich_text_node_id || data?.rich_text_node_id
            : type === "attachment"
            ? newData?.attachment_tree_node_id || data?.attachment_tree_node_id
            : type === "video"
            ? newData?.video_tree_node_id ||
              data?.video_tree_node_id ||
              newData?.video_id ||
              data?.video_id
            : newData?.rich_text_node_id ||
              data?.rich_text_node_id ||
              newData?.video_tree_node_ref ||
              data?.video_tree_node_ref
        }
        sourceSubstepIndex={data?.sourceSubstepIndex}
        sourceStepId={data.sourceStepId}
        data={{
          ...data,
          // Pass the actual fetched data for the specific element type
          newData: newData,
          // Pass the actual element data based on type
          attachments: type === "attachment" ? newData : data.attachments,
          richTexts: type === "rich-text" ? newData : data.richTexts,
          videos: type === "video" ? newData : data.videos,
          canvases: type === "canvas" ? newData : data.canvases,
          forms: type === "form" ? newData : data.forms,
        }}
        type={data.type}
        status={data.status}
        description={data.description}
        partNumbers={data.partNumbers}
        steps={data.steps}
        lastChildSibling={parseInt(lastChildSibling || 0) + 1 || 1}
        item_code_type={type}
        move_rev_node_info={data?.move_rev_node_info || {}}
      />
      {/* {renderDebugInfo()} */}

      {isLoading ? (
        <Loader text={`Loading ${type} data...`} />
      ) : (
        <>
          {type === "rich-text" && (
            <div style={{ padding: "20px" }}>
              {newData ? (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "flex",
                      gap: "8px",
                      cursor: "pointer",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input type="checkbox" checked={true} readOnly />
                      <h2 style={{ margin: 0, fontSize: "23px" }}>{title}</h2>
                    </div>
                    <div
                      style={{
                        color: "#888",
                        fontSize: "14px",
                        fontWeight: "normal",
                      }}
                    >
                      {substepTreePath}
                    </div>
                  </label>
                  <div style={{ marginLeft: "5px", marginTop: "5px" }}>
                    {newData.short_description}
                  </div>
                  <div style={{ marginLeft: "4px", marginTop: "5px" }}>
                    {newData.category_info?.category_name}
                  </div>
                  <div
                    style={{
                      fontSize: "14px",
                      marginLeft: "4px",
                      marginTop: "5px",
                    }}
                  >
                    {newData.rich_text_content ? (
                      <div
                        className="rich-text-content-display"
                        style={{ marginTop: "-6px" }}
                      >
                        <div className="editor-shell">
                          <div
                            style={{ clear: "both", minHeight: "20px" }}
                            dangerouslySetInnerHTML={{
                              __html: newData.rich_text_content,
                            }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              ) : null}
            </div>
          )}
          {type === "attachment" && (
            <div style={{ padding: "20px", clear: "both" }}>
              {newData && Array.isArray(newData.attachments) && (
                <div>
                  {/* Short description */}
                  <div
                    style={{
                      clear: "both",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input type="checkbox" checked={true} readOnly />
                      <h2 style={{ margin: 0, fontSize: "23px" }}>{title}</h2>
                    </div>
                    <div
                      style={{
                        color: "#888",
                        fontSize: "14px",
                        fontWeight: "normal",
                      }}
                    >
                      {substepTreePath}
                    </div>
                  </div>
                  <div
                    style={{
                      marginLeft: "5px",
                      marginBottom: "15px",
                      marginTop: "15px",
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {newData.short_description}
                  </div>

                  {/* Render each attachment */}
                  {newData.attachments.map((attachment, index) => (
                    <div
                      key={`attachment-${index}`}
                      style={{ marginLeft: "5px", marginBottom: "20px" }}
                    >
                      <div style={{ marginBottom: "10px" }}>
                        {attachment.description}
                      </div>
                      {renderPreview(attachment, `Attachment ${index + 1}`)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          {type === "video" && (
            <div style={{ padding: "20px" }}>
              {/* Main Video */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "10px",
                    justifyContent: "space-between",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <input type="checkbox" checked={true} readOnly />
                    <h2 style={{ margin: 0, fontSize: "23px" }}>{title}</h2>
                  </div>
                  <div
                    style={{
                      color: "#888",
                      fontSize: "14px",
                      fontWeight: "normal",
                    }}
                  >
                    {substepTreePath}
                  </div>
                </div>
                {newData ? (
                  <div style={{ marginLeft: "5px" }}>
                    {renderVideo1(newData)}
                  </div>
                ) : null}
              </div>
            </div>
          )}
          {type === "canvas" && (
            <div style={{ padding: "20px" }}>
              {/* Main Canvas */}
              {newData ? (
                <div style={{ marginBottom: "20px" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      paddingBottom: "10px",
                      justifyContent: "space-between",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input type="checkbox" checked={true} readOnly />
                      <h2 style={{ margin: 0, fontSize: "23px" }}>{title}</h2>
                    </div>
                    <div
                      style={{
                        color: "#888",
                        fontSize: "14px",
                        fontWeight: "normal",
                      }}
                    >
                      {substepTreePath}
                    </div>
                  </div>
                  <div style={{ marginLeft: "5px", marginTop: "10px" }}>
                    <div style={{ marginBottom: "10px" }}>
                      {newData.short_description}
                    </div>

                    {/* Get pages from canvas data */}
                    {(() => {
                      const getPages = (canvasData) => {
                        if (
                          !canvasData.pages ||
                          !Array.isArray(canvasData.pages)
                        ) {
                          // Fallback to old structure - create a single page with shapes
                          return [
                            {
                              canvas_page_id: "default",
                              shapes: canvasData.shapes || [],
                              layout_config: { layout: { columns: [] } },
                            },
                          ];
                        }
                        return canvasData.pages;
                      };

                      const pages = getPages(newData);

                      return <CanvasDisplay pages={pages} />;
                    })()}
                  </div>
                </div>
              ) : null}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SelectedSectionElement;
